import { useId, useState } from 'react'

import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  type AutoBidNotOrderedMarket,
  type AutoBidNotOrderedNotice,
  AutoBidNotOrderedReason,
} from '@ebalance/proto/bffuser/v1/auto_bid_notice_model_pb'
import { useTranslation } from 'react-i18next'

import { Alert, Box, Link, Text } from '@enechain/ecloud-designsystem'

import { areaLabelOf } from '~/components/bidding/common/areaLabel'
import { issuerTypeLabel } from '~/components/bidding/common/bidIssuer'
import {
  type AutoBidNoticeBannerRow,
  buildAutoBidNoticeBannerRows,
} from '~/models/autoBidNotOrderedBanner'
import { formatDateDisplay, fromProtoDate } from '~/models/dateTime'
import { komaNumberTimeRange } from '~/models/komaToTime'

/** 理由 → i18n key。UNSPECIFIED (BFF が未知の値を落とした時) は理由なし (`undefined`)。 */
const REASON_KEYS: Record<AutoBidNotOrderedReason, string | undefined> = {
  [AutoBidNotOrderedReason.UNSPECIFIED]: undefined,
  [AutoBidNotOrderedReason.QUANTITY_LIMIT_EXCEEDED]: 'quantityLimitExceeded',
  [AutoBidNotOrderedReason.STEP_COUNT_LIMIT_EXCEEDED]: 'stepCountLimitExceeded',
  [AutoBidNotOrderedReason.PRICE_CROSSING]: 'priceCrossing',
  [AutoBidNotOrderedReason.OPPOSITE_AUTO_BID_REMAINS]: 'oppositeAutoBidRemains',
  [AutoBidNotOrderedReason.PRICE_OUT_OF_RANGE]: 'priceOutOfRange',
  [AutoBidNotOrderedReason.MANUAL_BID_CREATED]: 'manualBidCreated',
  [AutoBidNotOrderedReason.DEMAND_FORECAST_NOT_CONFIRMED]:
    'demandForecastNotConfirmed',
  [AutoBidNotOrderedReason.NOT_DELIVERED_BY_DEADLINE]: 'notDeliveredByDeadline',
  [AutoBidNotOrderedReason.REFERENCE_VALUE_UNAVAILABLE]:
    'referenceValueUnavailable',
}

/** 開く前に出す行の最大件数 (AC-BIN-117 / AC-BSP-120)。 */
const INITIAL_ROWS = 10

type Props = {
  /** layout が取得した記録 (全市場)。 部品が `market` の分だけに絞る。 */
  notices: readonly AutoBidNotOrderedNotice[]
  /** 出す市場。 絞り忘れを型で防ぐため、 絞り込みは部品が持つ。 */
  market: AutoBidNotOrderedMarket
}

/**
 * 自動入札が発注しなかった札の警告バナー (スポット AC-BSP-120 / 時間前 AC-BIN-117、
 * AC-APF-069)。DS `Alert` の warning で、バナーそのものを消す操作は持たない。一覧が空なら何も描画しない。
 * 見出しの件数は一覧の行 (札) の数。11 件以上は先頭 10 件と「ほか {M}件」を出し、押すと残りを開く。
 * 開いた後は同じ位置に「閉じる」を出し、先頭 10 件に戻せる (Des 判断 2026-10-14: 常設のバナーなので、
 * 開いたままだと入札の画面を押し下げ続けるため)。開閉は同じボタンの表示を切り替えるので、
 * キーボードで押してもフォーカスはボタンに残る。
 * 1 行は design `_mountAutoBidSkipBanner` の
 * 「受渡日 ・ エリア ・ 発注主体 ・ コマ ・ 売買: 理由 (相手: 発注主体)」。同じ札に理由が複数ある時は
 * 1 行にまとめて理由をつなぐ。
 * 理由が UNSPECIFIED の記録は、実在する記録なので「理由なしの行」として出す (件数にも含める)。
 *
 * 利用 page は今は時間前入札だけだが、スポット入札 (#10333) が続けて使うため、
 * `CLAUDE.md` の「複数 page で使われてから共有へ昇格」を先取りして共有 directory に置く。
 */
export const AutoBidNotOrderedBanner = ({
  notices,
  market,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const listId = useId()
  const rows = buildAutoBidNoticeBannerRows(notices, market)
  if (rows.length === 0) return null
  const shown = isExpanded ? rows : rows.slice(0, INITIAL_ROWS)
  const restCount = rows.length - INITIAL_ROWS
  const prefix = 'common:bidding.autoBidNotOrdered'

  const reasonText = (n: AutoBidNotOrderedNotice): string => {
    const reasonKey = REASON_KEYS[n.reason]
    if (reasonKey === undefined) return ''
    const counterparty =
      n.counterpartyIssuerTypes.length === 0
        ? ''
        : ` ${t(`${prefix}.counterparty`, {
            issuers: n.counterpartyIssuerTypes
              .map(issuerTypeLabel)
              .join(t(`${prefix}.counterpartySeparator`)),
          })}`
    return `${t(`${prefix}.reason.${reasonKey}`)}${counterparty}`
  }

  const rowText = (row: AutoBidNoticeBannerRow): string => {
    const n = row.notices[0]
    const head = [
      n.targetDate === undefined
        ? ''
        : formatDateDisplay(fromProtoDate(n.targetDate)),
      areaLabelOf(n.areaCode) ?? '',
      issuerTypeLabel(n.issuerType),
      t(`${prefix}.slot`, {
        koma: n.timeCode,
        range: komaNumberTimeRange(n.timeCode),
      }),
      n.side === Side.BUY
        ? t('common:tradeSide.buy')
        : n.side === Side.SELL
          ? t('common:tradeSide.sell')
          : '',
    ]
      .filter((s) => s !== '')
      .join(' ・ ')
    const reasons = row.notices
      .map(reasonText)
      .filter((s) => s !== '')
      .join(t(`${prefix}.reasonSeparator`))
    return reasons === '' ? head : `${head}: ${reasons}`
  }

  return (
    // [DS差異] アイコンの縦位置: 一覧で縦に伸びるバナーなので、DS `Alert` の既定 (アイコンは上下中央) で
    // なく見出しの行に揃える (design `_mountAutoBidSkipBanner` の `align-items:flex-start` と
    // アイコンの `margin-top:2px`)。DS `Alert` はアイコンに props を渡せないので子セレクタで当てる。
    // `flexShrink={0}`: 高さ拘束された column flex に置かれても、 DS `Alert` (`overflow: hidden`) が
    // 押し潰されて切り落とされないようにする (同じ画面の CrossDatePendingApprovalBanner と同じ)。
    <Alert
      flexShrink={0}
      css={{ '& > svg': { alignSelf: 'flex-start', marginTop: '2px' } }}
      description={
        <>
          <Text>{t(`${prefix}.body`)}</Text>
          <Box
            as="ul"
            marginTop={1}
            paddingLeft="1.25em"
            fontVariantNumeric="tabular-nums"
            id={listId}
            listStyleType="disc"
          >
            {shown.map((row) => (
              <li key={row.key}>{rowText(row)}</li>
            ))}
          </Box>
          {restCount > 0 && (
            // 押すと一覧が開閉するテキストの導線なので DS `Link` に寄せる (design `.ecl-text-link`)。
            // 遷移ではなく開閉なので、 中身は button にする (asChild)。
            <Link marginTop={1} asChild>
              <button
                aria-controls={listId}
                aria-expanded={isExpanded}
                onClick={() => setIsExpanded((v) => !v)}
                type="button"
              >
                {isExpanded
                  ? t('common:action.close')
                  : t(`${prefix}.more`, { count: restCount })}
              </button>
            </Link>
          )}
        </>
      }
      status="warning"
      title={t(`${prefix}.title`, { count: rows.length })}
    />
  )
}
